<template>
  <div id="container">
    <hilo-button styleType="back" />
    <div class="row">
      <h1>Settings</h1>
    </div>
    <div class="row">
      <div id="settingsList" class="col-sm-1">
        <a @click="toggleShowEventTypes">Event Types</a>
        <a @click="toggleShowRooms">Rooms</a>
      </div>
      <div class="col-sm-3" v-if="showRooms">
        <hilo-button
          @click="openCreateModal"
          label="Add Room"
          style="margin-bottom: 10px"
        />
        <hilo-table :columns="roomsColumns" :data="rooms" />
      </div>
      <div class="col-sm-5" v-if="showEventTypes">
        <hilo-table
          v-if="showEventTypes"
          :columns="eventTypesColumns"
          :data="eventTypes"
        />
      </div>
    </div>

    <!-- Create Room Modal -->
    <HiloModal
      :isVisible="isCreateModalVisible"
      title="Add New Room"
      :showCloseButton="true"
      :hasFooter="true"
      :footerButtons="createFooterButtons"
      @close="closeCreateModal"
    >
      <template v-slot:header>
        <h4>Add New Room</h4>
      </template>

      <div>
        <div class="form-group">
          <label>Room Name</label>
          <input
            v-model="newRoom.name"
            class="form-control"
            placeholder="Room Name"
          />
        </div>
        <div class="form-group">
          <label>Occupants</label>
          <input
            v-model.number="newRoom.occupants"
            class="form-control"
            type="number"
            placeholder="Occupants"
            min="1"
          />
        </div>
      </div>
    </HiloModal>

    <!-- Edit Room Modal -->
    <HiloModal
      :isVisible="isEditModalVisible"
      title="Edit Room"
      :showCloseButton="true"
      :hasFooter="true"
      :footerButtons="editFooterButtons"
      @close="closeEditModal"
    >
      <template v-slot:header>
        <h4>Edit Room: {{ selectedRoom.name }}</h4>
      </template>

      <div>
        <div class="form-group">
          <label>Room Name</label>
          <input
            v-model="selectedRoom.name"
            class="form-control"
            placeholder="Room Name"
          />
        </div>
        <div class="form-group">
          <label>Occupants</label>
          <input
            v-model.number="selectedRoom.occupants"
            class="form-control"
            type="number"
            placeholder="Occupants"
            min="1"
          />
        </div>
      </div>
    </HiloModal>
    <!-- Edit Event Type Modal -->
    <HiloModal
      :isVisible="isEditEventTypeModalVisible"
      title="Edit Event Type"
      :showCloseButton="true"
      :hasFooter="true"
      :footerButtons="eventTypeFooterButtons"
      @close="closeEventTypeModal"
    >
      <template v-slot:header>
        <h4>Edit Event Type: {{ selectedEventType.name }}</h4>
      </template>

      <div>
        <div class="form-group">
          <label>Scheduled Color</label>
          <input
            type="color"
            v-model="selectedEventType.scheduledColor"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label>Started Color</label>
          <input
            type="color"
            v-model="selectedEventType.startedColor"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label>Completed Color</label>
          <input
            type="color"
            v-model="selectedEventType.completedColor"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label>Text Color</label>
          <input
            type="color"
            v-model="selectedEventType.textColor"
            class="form-control"
          />
        </div>
      </div>
    </HiloModal>
  </div>
</template>

<script>
import HiloButton from "src/components/HiloComponents/HiloButton.vue";
import HiloTable from "src/components/HiloComponents/HiloTable.vue";
import HiloModal from "src/components/HiloComponents/HiloModal.vue";
import EventRoomsAPIService from "../../../../servicehandlers/EventRoomsAPIService.js";
import EventTypesAPIService from "@/servicehandlers/EventTypesAPIService";

const eventRoomsAPIService = new EventRoomsAPIService();
const eventTypesAPIService = new EventTypesAPIService();

export default {
  components: {
    HiloButton,
    HiloTable,
    HiloModal,
  },
  data() {
    return {
      eventTypes: [],
      rooms: [],
      selectedEventType: {},
      selectedRoom: {},
      newRoom: { name: "", occupants: 1 },
      showRooms: false,
      showEventTypes: false,
      isEditEventTypeModalVisible: false,
      eventTypeFooterButtons: [
        {
          label: "Cancel",
          type: "default",
          onClick: this.closeEventTypeModal,
        },
        {
          label: "Update",
          type: "greenCheck",
          onClick: this.updateEventType,
        },
      ],
      isCreateModalVisible: false,
      isEditModalVisible: false,
      createFooterButtons: [
        {
          label: "Cancel",
          type: "default",
          onClick: this.closeCreateModal,
        },
        {
          label: "Save",
          type: "greenCheckButton",
          onClick: this.createRoom,
        },
      ],
      editFooterButtons: [
        {
          label: "Cancel",
          type: "default",
          onClick: this.closeEditModal,
        },
        {
          label: "Delete",
          type: "danger", // Red delete button
          onClick: this.deleteRoom,
        },
        {
          label: "Update",
          type: "greenCheckButton",
          onClick: this.updateRoom,
        },
      ],
      roomsColumns: [
        { key: "name", label: "Name", backgroundColor: "#3399ff" },
        { key: "occupants", label: "Occupants", backgroundColor: "#ffc500" },
        {
          key: "edit",
          label: "Edit",
          backgroundColor: "#9f9f9f",
          component: "HiloButton",
          componentProps: (row) => ({
            styleType: "editIcon",
            onClick: () => this.openEditModal(row),
          }),
        },
      ],
      eventTypesColumns: [
        { key: "name", label: "Name", backgroundColor: "#9f9f9f" },
        {
          key: "scheduledColorHTML",
          label: "Scheduled Color",
          backgroundColor: "#9f9f9f",
          isHtml: true,
        },
        {
          key: "startedColorHTML",
          label: "Started Color",
          backgroundColor: "#9f9f9f",
          isHtml: true,
        },
        {
          key: "completedColorHTML",
          label: "Completed Color",
          backgroundColor: "#9f9f9f",
          isHtml: true,
        },
        {
          key: "textColorHTML",
          label: "Text Color",
          backgroundColor: "#9f9f9f",
          isHtml: true,
        },
        {
          key: "edit",
          label: "Edit",
          backgroundColor: "#9f9f9f",
          component: "HiloButton",
          componentProps: (row) => ({
            styleType: "editIcon",
            onClick: (row) => this.openEventTypesModal(row),
          }),
        },
      ],
    };
  },
  async created() {
    await this.getEventTypes();
    await this.getRooms();
  },
  methods: {
    async getEventTypes() {
      return eventTypesAPIService
        .get(this.$router)
        .then((response) => {
          for (let type of response) {
            let typeScheduledColor = type.scheduledColor;
            let typeStartedColor = type.startedColor;
            let typeCompletedColor = type.completedColor;
            let typeTextColor = type.textColor;

            type.scheduledColorHTML = `<div style="background-color:${typeScheduledColor};color:${typeTextColor};">${typeScheduledColor}</div>`;
            type.startedColorHTML = `<div style="background-color:${typeStartedColor};color:${typeTextColor};">${typeStartedColor}</div>`;
            type.completedColorHTML = `<div style="background-color:${typeCompletedColor};color:${typeTextColor};">${typeCompletedColor}</div>`;
            type.textColorHTML = `<div style="background-color:#9f9f9f;color:${typeTextColor};">${typeTextColor}</div>`;
          }
          this.eventTypes = response;
        })
        .catch((error) => {
          this.error_txt = "Failed to get rooms: " + error;
        });
    },
    async getRooms() {
      return eventRoomsAPIService
        .get(this.$router)
        .then((response) => {
          this.rooms = response;
        })
        .catch((error) => {
          this.error_txt = "Failed to get rooms: " + error;
        });
    },
    toggleShowEventTypes() {
      this.showRooms = false;
      this.showEventTypes = true;
    },
    toggleShowRooms() {
      this.showEventTypes = false;
      this.showRooms = true;
    },
    // ROOMS
    openCreateModal() {
      this.newRoom = { name: "", occupants: 1 }; // Reset new room data
      this.isCreateModalVisible = true;
    },
    closeCreateModal() {
      this.isCreateModalVisible = false;
    },
    openEditModal(room) {
      this.selectedRoom = { ...room }; // Clone selected room
      this.isEditModalVisible = true;
    },
    closeEditModal() {
      this.isEditModalVisible = false;
    },
    async createRoom() {
      return eventRoomsAPIService
        .create(this.newRoom, this.$router)
        .then((createdRoom) => {
          this.rooms.push(createdRoom);
          this.toast(
            "b-toaster-bottom-right",
            `Successfully created \"${createdRoom.name}\" room!`,
            "success",
            true,
          );
          this.closeCreateModal();
        })
        .catch((error) => {
          this.error_txt = "Failed to create room: " + error;
        });
    },
    async updateRoom() {
      return eventRoomsAPIService
        .update(this.selectedRoom.id, this.selectedRoom, this.$router)
        .then((updated) => {
          const index = this.rooms.findIndex(
            (room) => room.id === this.selectedRoom.id,
          );
          if (index !== -1) {
            this.rooms.splice(index, 1, { ...this.selectedRoom });
          }
          this.toast(
            "b-toaster-bottom-right",
            `Successfully updated \"${updated.name}\" room!`,
            "success",
            true,
          );
          this.closeEditModal();
        })
        .catch((error) => {
          this.error_txt = "Failed to update room: " + error;
        });
    },
    async deleteRoom() {
      return eventRoomsAPIService
        .delete(this.selectedRoom.id, this.$router)
        .then(() => {
          const index = this.rooms.findIndex(
            (room) => room.id === this.selectedRoom.id,
          );
          if (index !== -1) {
            this.rooms.splice(index, 1);
          }
          this.toast(
            "b-toaster-bottom-right",
            `Successfully deleted room!`,
            "success",
            true,
          );
          this.closeEditModal();
        })
        .catch((error) => {
          this.error_txt = "Failed to delete room: " + error;
        });
    },

    // EVENT TYPES
    openEventTypesModal(eventType) {
      this.selectedEventType = { ...eventType }; // Clone selected event type
      this.isEditEventTypeModalVisible = true;
    },
    closeEventTypeModal() {
      this.isEditEventTypeModalVisible = false;
    },
    async updateEventType() {
      return eventTypesAPIService
        .update(this.selectedEventType.id, this.selectedEventType, this.$router)
        .then((updated) => {
          const index = this.eventTypes.findIndex(
            (eventType) => eventType.id === this.selectedEventType.id,
          );
          if (index !== -1) {
            let typeScheduledColor = updated.scheduledColor;
            let typeStartedColor = updated.startedColor;
            let typeCompletedColor = updated.completedColor;
            let typeTextColor = updated.textColor;

            updated.scheduledColorHTML = `<div style="background-color:${typeScheduledColor};color:${typeTextColor};">${typeScheduledColor}</div>`;
            updated.startedColorHTML = `<div style="background-color:${typeStartedColor};color:${typeTextColor};">${typeStartedColor}</div>`;
            updated.completedColorHTML = `<div style="background-color:${typeCompletedColor};color:${typeTextColor};">${typeCompletedColor}</div>`;
            updated.textColorHTML = `<div style="background-color:#9f9f9f;color:${typeTextColor};">${typeTextColor}</div>`;
            this.eventTypes.splice(index, 1, { ...updated });
          }
          this.toast(
            "b-toaster-bottom-right",
            `Successfully updated event type!`,
            "success",
            true,
          );
          this.closeEventTypeModal();
        })
        .catch((error) => {
          this.error_txt = "Failed to update event type: " + error;
        });
    },
    toast(toaster, message, variant, append = false) {
      this.$bvToast.toast(`${message}`, {
        title: `Success`,
        toaster: toaster,
        solid: true,
        variant: variant,
        appendToast: append,
      });
    },
  },
};
</script>

<style scoped>
#container {
  margin-left: 10px;
}
#settingsList {
  display: flex;
  flex-direction: column;
}
</style>
